/** 
 * @name 广告 Mixin
 * @description 负责广告脚本加载和初始化，处理广告元素的可视区域检测(Intersection Observer)，管理广告加载重试机制
 */
export default {
	data() {
		return {
			adObserver: null,
			observedAds: new Set(),
			adLoadRetries: new Map(),
			maxRetries: 3,
			preloadDistance: 300, // 改为300px的预加载距离
		}
	},

	methods: {
		/** 初始化广告系统 */
		async initAdSystem() {
			console.log(`[AdMixin] Initializing ad system...`, {
				componentName: this.$options.name,
				timestamp: new Date().toISOString(),
			})
			if (typeof window !== 'undefined') {
				await this.loadAdSenseScript()
				this.$nextTick(() => {
					setTimeout(() => {
						this.setupAdObserver()
					}, 100)
				})
			}
		},
		/** 加载 AdSense 脚本 */
		async loadAdSenseScript() {
			if (window.adsbygoogle?.loaded) {
				this.$eventrack('adscript_loaded', 'expose')
				return
			}

			if (!this.adsensConfig?.scriptUrl) {
				this.$eventrack('no_adscript_config', 'expose')
				return
			}

			try {
				await this.loadScript(this.adsensConfig.scriptUrl)
				this.$eventrack('adscript_add_success', 'expose')
			} catch (error) {
				console.error('Failed to load AdSense script:', error)
				this.$eventrack('adscript_load_error', 'error')
			}
		},
		/** 加载脚本 */
		loadScript(url) {
			return new Promise((resolve, reject) => {
				const script = document.createElement('script')
				script.src = url
				script.async = true
				script.crossOrigin = 'anonymous'
				script.onload = resolve
				script.onerror = reject
				document.head.appendChild(script)
			})
		},
		/** 设置广告观察器 */
		setupAdObserver() {
			if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
				return
			}

			if (this.adObserver) {
				this.adObserver.disconnect()
			}

			this.observedAds = new Set()

			console.log(`准备监听广告，预加载距离:${this.preloadDistance}`)

			this.adObserver = new IntersectionObserver(
				(entries) => {
					console.log('观察对象⬇️')
					console.log(entries)
					entries.forEach(this.handleAdIntersection)
				},
				{
					rootMargin: `${this.preloadDistance}px 0px 0px 0px`,
					threshold: 0,
				},
			)

			this.$nextTick(() => {
				setTimeout(() => {
					this.observeAds()
				}, 200)
			})
		},
		/** 观察广告元素 */
		observeAds() {
			const adComponents = Object.entries(this.$refs)
				.filter(([key]) => key.startsWith('ads-'))
				.flatMap(([, ref]) => (Array.isArray(ref) ? ref : [ref]))
				.filter(Boolean)

			console.log(`找到了:${adComponents.length}个广告组件`)

			if (adComponents.length > 0) {
				adComponents.forEach((component) => {
					const adElement = component.$el.querySelector('ins')
					if (adElement && !adElement.getAttribute('data-ad-status') && !this.observedAds.has(adElement)) {
						const rect = adElement.getBoundingClientRect()
						const position = component.$el.getAttribute('data-ad-position')
						const index = component.$el.getAttribute('data-ad-index')

						console.log(`监听到新的广告元素:`, {
							element: adElement,
							position: {
								top: rect.top + window.pageYOffset,
								distanceToViewport: rect.top - window.innerHeight,
								preloadPoint: rect.top - this.preloadDistance,
							},
							componentInfo: {
								position,
								index,
							},
						})

						this.observedAds.add(adElement)
						this.adObserver.observe(adElement)
					}
				})
			}
		},
		/** 处理广告元素进入预加载区域 */
		handleAdIntersection(entry) {
			if (!entry || !this.observedAds) return

			const adElement = entry.target
			if (entry.isIntersecting && adElement && !adElement.getAttribute('data-ad-status')) {
				const rect = adElement.getBoundingClientRect()
				console.log(`广告元素进入预加载区域:`, {
					element: adElement,
					position: {
						top: rect.top + window.pageYOffset,
						distanceToViewport: rect.top - window.innerHeight,
						preloadPoint: rect.top - this.preloadDistance,
					},
					intersectionRatio: entry.intersectionRatio,
				})

				this.adObserver.unobserve(adElement)
				this.observedAds.delete(adElement)
				this.loadAd(adElement)
			}
		},
		/** 加载广告 */
		async loadAd(adElement, retryCount = 0) {
			if (adElement.getAttribute('data-ad-status')) {
				return
			}

			if (!window.adsbygoogle) {
				if (retryCount < this.maxRetries) {
					console.log(`Retrying ad load (${retryCount + 1}/${this.maxRetries})...`)
					setTimeout(() => {
						this.loadAd(adElement, retryCount + 1)
					}, 1000)
					return
				}
				console.warn('AdSense 加载失败')
				return
			}

			try {
				if (adElement && document.body.contains(adElement)) {
					await this.$nextTick()
					if (adElement.offsetWidth > 0) {
						console.log(`推送广告:`, {
							element: adElement,
							width: adElement.offsetWidth,
							slot: adElement.getAttribute('data-ad-slot'),
							status: 'loading',
						})
						;(window.adsbygoogle = window.adsbygoogle || []).push({})
						adElement.setAttribute('data-ad-status', 'loaded')
					} else {
						console.log(`广告元素没有宽度,等待...`, adElement)
						if (retryCount < this.maxRetries) {
							setTimeout(() => {
								this.loadAd(adElement, retryCount + 1)
							}, 500)
						}
					}
				}
			} catch (error) {
				console.error(`广告推送错误:`, error)
				if (retryCount < this.maxRetries) {
					setTimeout(() => {
						this.loadAd(adElement, retryCount + 1)
					}, 1000)
				}
			}
		},
		/** 清理广告系统 */
		cleanupAdSystem() {
			if (this.adObserver) {
				this.adObserver.disconnect()
				this.adObserver = null
			}
			this.observedAds.clear()
		},
	},

	beforeDestroy() {
		this.cleanupAdSystem()
	},
}
